import { useEffect, useState } from "react";
import {
  requestRaffleById,
  requestRaffleInit,
} from "@/apps/raffle/services/raffleApi";
import { RaffleInitResponse, RaffleParticipant } from "@/apps/raffle/types";
import { useRaffleDataStore } from "@/apps/raffle/store/raffleDataStore";
import { sortParticipantsByChance } from "./utils";

export const useRaffleData = (yandexId: string | null, bonusId?: string) => {
  const getCachedRaffle = useRaffleDataStore((state) => state.getRaffle);
  const cacheRaffle = useRaffleDataStore((state) => state.setRaffle);
  const [raffle, setRaffle] = useState<RaffleInitResponse | null>(() =>
    getCachedRaffle(yandexId, bonusId)
  );
  const [participants, setParticipants] = useState<RaffleParticipant[]>(() =>
    sortParticipantsByChance(
      getCachedRaffle(yandexId, bonusId)?.participants ?? []
    )
  );
  const [isLoading, setIsLoading] = useState(
    () => !getCachedRaffle(yandexId, bonusId)
  );

  useEffect(() => {
    let isCurrentRequest = true;

    const loadRafflePage = async () => {
      if (!yandexId) {
        setRaffle(null);
        setParticipants([]);
        setIsLoading(false);
        return;
      }

      const cachedRaffle = getCachedRaffle(yandexId, bonusId);

      if (cachedRaffle) {
        setRaffle(cachedRaffle);
        setParticipants(sortParticipantsByChance(cachedRaffle.participants));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const raffleData = bonusId
          ? await requestRaffleById(bonusId, yandexId)
          : await requestRaffleInit(yandexId);

        if (!isCurrentRequest) return;

        setRaffle(raffleData);
        setParticipants(sortParticipantsByChance(raffleData.participants));
        cacheRaffle(yandexId, bonusId, raffleData);
      } catch {
        if (!isCurrentRequest) return;

        setRaffle(null);
        setParticipants([]);
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };

    loadRafflePage();

    return () => {
      isCurrentRequest = false;
    };
  }, [bonusId, cacheRaffle, getCachedRaffle, yandexId]);

  return {
    raffle,
    participants,
    isLoading,
  };
};
